import {
  Alert,
  Badge,
  Box,
  Button,
  FileButton,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { AppShell } from '../components/app-shell/AppShell';
import { MotionSection, PageHero } from '../components/ui';
import { fetchOk } from '../lib/api';
import { csrfHeaders, useAuth } from '../lib/auth-context';
import { showToast } from '../lib/toast';
import { uploadFileBundle } from '../lib/upload';

/**
 * /verification — verification status + the path to a Verified badge.
 *
 * Two viewers, two very different paths (Verification & Safety MVP §2):
 *   - ARTIST  → verified AUTOMATICALLY. No ID, no selfie, no waiting. The
 *               trifecta is mobile SMS-verified + a profile photo + ≥1 link;
 *               this page is a live checklist of those three signals with
 *               inline CTAs. The worker mints the approved row the moment the
 *               third box is ticked (services/artist-auto-verify.ts).
 *   - HOST    → the existing government-ID flow (ID + a photo of you holding
 *               it), posted in one multipart request to /api/verification/me.
 *
 * The viewer is chosen from the active hat, mirroring the worker's
 * /me/verification flow resolution, so a dual-role user sees the path that
 * matches the hat they're wearing.
 *
 * Voice: forward-looking. "Submitted. We aim to review within 72 hours." after
 * a host submission; the artist path stays encouraging and never blocks browse
 * or messaging.
 */

type Verification = {
  id: string;
  subject_kind: 'artist' | 'host_user' | 'venue';
  subject_id: string;
  state: 'pending' | 'approved' | 'rejected' | 'revoked';
  submitted_at: number;
  rejection_reason: string | null;
  revocation_reason: string | null;
  expires_at: number | null;
  // AUD-031: present on venue-subject rows once the worker joins it in —
  // coded defensively (optional + fallback) so this page doesn't break if it
  // ships first.
  venue_name?: string | null;
};

type ListResponse = { ok: true; verifications: Verification[] };

const STATE_BADGE: Record<Verification['state'], { label: string; color: string }> = {
  pending: { label: 'In review', color: 'blue' },
  approved: { label: 'Verified', color: 'teal' },
  rejected: { label: 'Rejected', color: 'red' },
  revoked: { label: 'Revoked', color: 'red' },
};

export const Route = createFileRoute('/verification')({
  component: VerificationPage,
});

function VerificationPage() {
  const { capabilities, activeHat, isLoading } = useAuth();

  if (isLoading) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  // Mirror the worker's /me/verification flow resolution: the active hat wins,
  // falling back to the most-capable hat. Artists get the automatic path; hosts
  // (and admins, who verify as a host_user) keep the government-ID flow.
  const flow: 'artist' | 'host' | 'admin' =
    activeHat ??
    (capabilities?.isAdmin ? 'admin' : capabilities?.isHost ? 'host' : 'artist');

  return flow === 'artist' ? <ArtistVerification /> : <HostVerification />;
}

// ---------------------------------------------------------------------------
// Artist — the automatic path. A live checklist of the auto-verify trifecta.
// ---------------------------------------------------------------------------

type AccountIdentity = {
  ok: boolean;
  account: {
    mobileVerifiedAt: string | null;
    // AUD-006: the confirm gate also checks these — extend the checklist
    // rather than let the badge claim more than the gate actually grants.
    legalFullName: string | null;
    emergencyContactName: string | null;
    emergencyContactPhone: string | null;
  };
};

type ArtistProfileMe = {
  ok: boolean;
  profile: {
    profile_photo_url: string | null;
    links: { kind: string; url: string }[];
    verified_at: number | null;
  };
};

function ArtistVerification() {
  const qc = useQueryClient();
  const accountQ = useQuery({
    queryKey: ['users', 'me', 'account'],
    queryFn: async (): Promise<AccountIdentity> => {
      const res = await fetch('/api/users/me/account', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load account');
      return res.json();
    },
  });

  const profileQ = useQuery({
    queryKey: ['profiles', 'me', 'artist'],
    queryFn: async (): Promise<ArtistProfileMe> => {
      const res = await fetch('/api/profiles/me/artist', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load profile');
      return res.json();
    },
  });

  const mobileVerified = accountQ.data?.account.mobileVerifiedAt != null;
  const photoUrl = profileQ.data?.profile.profile_photo_url ?? null;
  const hasPhoto = photoUrl !== null && photoUrl.length > 0;
  const hasLink = (profileQ.data?.profile.links.length ?? 0) > 0;
  const verifiedAt = profileQ.data?.profile.verified_at ?? null;
  // Verified-ness is driven ONLY by verified_at — the same signal the host's
  // confirm gate reads (FR-029). We must not claim "you're verified" off the
  // live trifecta while the booking gate, which sees the empty verifications
  // table, disagrees. A legacy account that has the trifecta but no row yet is
  // self-healed by the effect below on this same visit.
  const isVerified = verifiedAt !== null;
  const trifectaComplete = mobileVerified && hasPhoto && hasLink;

  // AUD-006: the confirm gate (POST /api/exchanges/:id/confirm) checks legal
  // name + emergency contact name/phone on top of the Verified trifecta —
  // neither field flips the Verified badge itself (only artist-auto-verify's
  // mobile+photo+link does), so they're tracked separately here and folded
  // into `confirmReady`, not `isVerified`.
  const hasLegalName = (accountQ.data?.account.legalFullName ?? '').trim().length > 0;
  const hasEmergencyContact =
    (accountQ.data?.account.emergencyContactName ?? '').trim().length > 0 &&
    (accountQ.data?.account.emergencyContactPhone ?? '').trim().length > 0;
  const identityComplete = hasLegalName && hasEmergencyContact;
  const confirmReady = isVerified && identityComplete;

  // Self-heal legacy accounts: an artist who completed the trifecta before
  // auto-verify shipped has no verifications row and no verified_at. Ask the
  // worker to (idempotently) re-run the check once, then refetch so the badge
  // and the booking gate agree. Fires at most once per mount, only when the
  // trifecta looks complete but verified_at is still null. Declared before the
  // loading early-return so the hook order stays stable across renders.
  const healedRef = useRef(false);
  useEffect(() => {
    if (healedRef.current || verifiedAt !== null || !trifectaComplete) return;
    healedRef.current = true;
    void (async () => {
      try {
        const res = await fetch('/api/verification/me/refresh-artist', {
          method: 'POST',
          credentials: 'include',
          headers: { ...csrfHeaders() },
        });
        if (res.ok) {
          await qc.invalidateQueries({ queryKey: ['profiles', 'me', 'artist'] });
        }
      } catch {
        // Fail-soft: the checklist just stays visible; nothing user-facing breaks.
      }
    })();
  }, [verifiedAt, trifectaComplete, qc]);

  if (accountQ.isPending || profileQ.isPending) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Trust & safety"
            title="Your verification"
            lede="Artists are verified automatically. No ID, no waiting. Tick three quick boxes and your Verified badge is yours."
          />
        </MotionSection>

        <Text c="dimmed">
          Being verified matters at one moment: when a host confirms your stay. Until then
          you can browse, message, and send requests freely.
        </Text>

        {isVerified ? (
          <Paper p="lg" withBorder radius="md">
            <Stack gap="md">
              <Group gap="sm" align="flex-start" wrap="nowrap">
                <ThemeIcon size={32} radius="xl" color="teal" variant="filled">
                  <Text fw={700} c="white">
                    ✓
                  </Text>
                </ThemeIcon>
                <Stack gap={4}>
                  <Group gap="xs">
                    <Title order={3}>You're verified</Title>
                    <Badge color="teal" variant="light">
                      Verified
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    Hosts can see you're the real deal.{' '}
                    {confirmReady
                      ? "You're ready to confirm a stay whenever the moment comes."
                      : "There's one more thing before a host can confirm a stay with you."}
                  </Text>
                </Stack>
              </Group>
              {/* AUD-006: the Verified badge is earned by the mobile+photo+link
                  trifecta alone — legal name and an emergency contact are a
                  separate, confirm-time requirement, so a Verified artist can
                  still be short of them. Don't let the badge overclaim. */}
              {!confirmReady && (
                <Alert color="brand" variant="light" title="One more thing before a host can confirm">
                  <Stack gap="xs">
                    <Text size="sm">
                      Hosts need your legal name and an emergency contact before they can
                      confirm a stay with you. Neither changes your Verified badge.
                    </Text>
                    <Group gap="xs">
                      <Button component={Link} to="/settings/account" size="xs">
                        Add these details
                      </Button>
                    </Group>
                  </Stack>
                </Alert>
              )}
            </Stack>
          </Paper>
        ) : (
          <Paper p="lg" withBorder radius="md">
            <Stack gap="lg">
              <Box>
                <Title order={3}>Five steps to confirm-ready</Title>
                <Text size="sm" c="dimmed" mt={4}>
                  The first three earn your Verified badge instantly — we check automatically,
                  so there's nothing to submit and no one to wait on. All five are what a host
                  needs before they can confirm a stay with you.
                </Text>
              </Box>
              <Stack gap="lg">
                <ChecklistStep
                  index={1}
                  done={mobileVerified}
                  emphasise
                  title="Verify your mobile number"
                  description="A quick SMS code from your account settings. This is the big one, so start here."
                  ctaLabel="Verify mobile"
                  ctaTo="/settings/account"
                />
                <ChecklistStep
                  index={2}
                  done={hasPhoto}
                  title="Add a profile photo"
                  description="One clear photo so hosts can put a face to your name."
                  ctaLabel="Add photo"
                  ctaTo="/profile/edit"
                />
                <ChecklistStep
                  index={3}
                  done={hasLink}
                  title="Add at least one link"
                  description="Your website, Instagram, Spotify, or Bandcamp. Anywhere a host can hear your work."
                  ctaLabel="Add a link"
                  ctaTo="/profile/edit"
                />
                <ChecklistStep
                  index={4}
                  done={hasLegalName}
                  title="Add your legal name"
                  description="For the emergency contact card only — hosts still see your display name everywhere else."
                  ctaLabel="Add legal name"
                  ctaTo="/settings/account"
                />
                <ChecklistStep
                  index={5}
                  done={hasEmergencyContact}
                  title="Add an emergency contact"
                  description="A name and phone number we can reach if something goes wrong on the road."
                  ctaLabel="Add emergency contact"
                  ctaTo="/settings/account"
                />
              </Stack>
            </Stack>
          </Paper>
        )}
      </Stack>
    </AppShell>
  );
}

function ChecklistStep({
  index,
  done,
  title,
  description,
  ctaLabel,
  ctaTo,
  emphasise = false,
}: {
  index: number;
  done: boolean;
  title: string;
  description: string;
  ctaLabel: string;
  ctaTo: '/settings/account' | '/profile/edit';
  emphasise?: boolean;
}) {
  return (
    <Group align="flex-start" gap="md" wrap="nowrap">
      <ThemeIcon
        size={28}
        radius="xl"
        variant={done ? 'filled' : 'light'}
        color={done ? 'teal' : 'ink.3'}
        style={{ flexShrink: 0, marginTop: 2 }}
      >
        {done ? (
          <Text size="sm" fw={700} c="white">
            ✓
          </Text>
        ) : (
          <Text size="sm" fw={700} c="ink.7">
            {index}
          </Text>
        )}
      </ThemeIcon>
      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
        <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
          <Text
            fw={600}
            c={done ? 'ink.6' : 'ink.9'}
            td={done ? 'line-through' : undefined}
          >
            {title}
          </Text>
          {done ? (
            <Badge color="teal" variant="light">
              Done
            </Badge>
          ) : (
            <Button
              component={Link}
              to={ctaTo}
              size="xs"
              variant={emphasise ? 'filled' : 'light'}
              style={{ flexShrink: 0 }}
            >
              {ctaLabel}
            </Button>
          )}
        </Group>
        {!done && (
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Host — the government-ID flow (unchanged). ID + a photo of you holding it,
// posted in one upload request. Venues verify from the venue edit screen.
// ---------------------------------------------------------------------------

// FR-145: go through fetchOk so a text/plain 401 or an HTML 500 throws an
// ApiError (a real query error the component branches on) instead of blowing up
// in `r.json()` and silently degrading to the empty, unsubmitted-looking form.
async function fetchVerifications(): Promise<ListResponse> {
  const r = await fetchOk('/api/verification/me/verification', { credentials: 'include' });
  return (await r.json()) as ListResponse;
}

async function submitVerification(payload: {
  idFile: File;
  selfieFile: File;
}): Promise<unknown> {
  // One gzipped raw-body bundle (lib/upload.ts): multipart photos, and PDFs in
  // any raw form, are blocked by the zone's firewall before they reach us.
  // Throws a clean ApiError with the server's message on non-2xx (FR-145).
  const r = await uploadFileBundle(
    '/api/verification/me/verification',
    [payload.idFile, payload.selfieFile],
    { subject_kind: 'host_user', evidence_kinds: 'id_document,selfie' },
  );
  return await r.json();
}

function HostVerification() {
  const qc = useQueryClient();
  const listQ = useQuery({ queryKey: ['verifications', 'me'], queryFn: fetchVerifications });

  const [idFile, setIdFile] = useState<File | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const idResetRef = useRef<() => void>(null);
  const selfieResetRef = useRef<() => void>(null);

  const submitM = useMutation({
    mutationFn: submitVerification,
    onSuccess: async () => {
      setIdFile(null);
      setSelfieFile(null);
      idResetRef.current?.();
      selfieResetRef.current?.();
      await qc.invalidateQueries({ queryKey: ['verifications', 'me'] });
      showToast({
        message: 'Submitted. We aim to review within 72 hours.',
        variant: 'success',
      });
    },
    onError: (err: Error) => showToast({ message: err.message, variant: 'error' }),
  });

  if (listQ.isPending) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  // FR-145: surface a failed load explicitly. Falling through to the empty
  // form here would invite a host with a verification already in flight to
  // submit a confusing duplicate.
  if (listQ.isError) {
    return (
      <AppShell tone="form">
        <Alert color="red" title="Couldn’t load your verification status">
          {listQ.error instanceof Error
            ? listQ.error.message
            : 'The request failed. Refresh to try again.'}
        </Alert>
      </AppShell>
    );
  }

  const list = listQ.data?.verifications ?? [];
  const ownStatus = list.find((v) => v.subject_kind === 'host_user');

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Trust & safety"
            title="Verify your identity"
            lede="Two minutes. We use this to give the artists you host confidence that you are who you say you are."
          />
        </MotionSection>

        {ownStatus && (
          <Paper p="md" withBorder radius="md">
            <Group justify="space-between" align="flex-start">
              <Stack gap={4}>
                <Title order={3}>Current status</Title>
                <Group gap="xs">
                  <Badge color={STATE_BADGE[ownStatus.state].color}>
                    {STATE_BADGE[ownStatus.state].label}
                  </Badge>
                  <Text size="sm" c="dimmed">
                    Submitted {new Date(ownStatus.submitted_at).toLocaleDateString()}
                  </Text>
                </Group>
              </Stack>
            </Group>
            {ownStatus.state === 'pending' && (
              <Text mt="sm" c="dimmed">
                We aim to review within 72 hours. We'll let you know either way.
              </Text>
            )}
            {ownStatus.state === 'revoked' && ownStatus.revocation_reason && (
              <Alert color="red" mt="sm" title="Reason for revocation">
                {ownStatus.revocation_reason}
              </Alert>
            )}
          </Paper>
        )}

        {ownStatus && ownStatus.state === 'rejected' && (
          <Alert color="red" variant="light" title="What we saw">
            {ownStatus.rejection_reason && ownStatus.rejection_reason.trim().length > 0
              ? `Your verification needed another look. ${ownStatus.rejection_reason}.`
              : 'Your verification needed another look. Please resubmit with both documents clearly visible.'}
          </Alert>
        )}

        {(!ownStatus || ownStatus.state !== 'pending') && (
          <Paper p="md" withBorder radius="md">
            <Stack gap="md">
              <Title order={3}>
                {ownStatus ? 'Re-submit verification' : 'Submit for review'}
              </Title>

              <Stack gap="sm">
                <Text fw={500}>Step 1: Government-issued ID</Text>
                <Text size="sm" c="dimmed">
                  Upload a clear photo of a driver's licence, passport, or national ID. We'll
                  never share this image, only that you're verified.
                </Text>
                <Group>
                  <FileButton
                    resetRef={idResetRef}
                    accept="image/png,image/jpeg,image/webp,application/pdf"
                    onChange={setIdFile}
                  >
                    {(props) => (
                      <Button {...props} variant={idFile ? 'light' : 'filled'}>
                        {idFile ? `Selected: ${idFile.name}` : 'Choose file'}
                      </Button>
                    )}
                  </FileButton>
                </Group>
              </Stack>

              <Stack gap="sm">
                <Text fw={500}>Step 2: A photo of you holding the ID</Text>
                <Text size="sm" c="dimmed">
                  This confirms the ID belongs to you.
                </Text>
                <Group>
                  <FileButton
                    resetRef={selfieResetRef}
                    accept="image/png,image/jpeg,image/webp"
                    onChange={setSelfieFile}
                  >
                    {(props) => (
                      <Button {...props} variant={selfieFile ? 'light' : 'filled'}>
                        {selfieFile ? `Selected: ${selfieFile.name}` : 'Choose file'}
                      </Button>
                    )}
                  </FileButton>
                </Group>
              </Stack>

              <Group justify="flex-end">
                <Button
                  disabled={!idFile || !selfieFile}
                  loading={submitM.isPending}
                  onClick={() => {
                    if (!idFile || !selfieFile) return;
                    submitM.mutate({ idFile, selfieFile });
                  }}
                >
                  Submit for review
                </Button>
              </Group>

              <Text size="xs" c="dimmed">
                We review within 72 hours. We'll let you know either way.
              </Text>
            </Stack>
          </Paper>
        )}

        {list.filter((v) => v.subject_kind === 'venue').length > 0 && (
          <Paper p="md" withBorder radius="md">
            <Stack gap="sm">
              <Title order={3}>Venue verifications</Title>
              {list
                .filter((v) => v.subject_kind === 'venue')
                .map((v) => (
                  <Group key={v.id} justify="space-between">
                    <Text size="sm">
                      {v.venue_name && v.venue_name.length > 0
                        ? v.venue_name
                        : `Venue ${v.subject_id.slice(0, 8)}…`}
                    </Text>
                    <Badge color={STATE_BADGE[v.state].color}>
                      {STATE_BADGE[v.state].label}
                    </Badge>
                  </Group>
                ))}
            </Stack>
          </Paper>
        )}
      </Stack>
    </AppShell>
  );
}
